import { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { CoverImage, InfoContent } from './components';

import Camiseta from '@/assets/Camiseta.png';
import IcStats from '@/assets/tournaments/ic_challenge_stats.svg';
import { Button, Modal } from '@/components';
import { Layout } from '@/components/GenericsTournaments';
import useAuth from '@/hooks/useAuth';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import useTourney from '@/hooks/useTourney';
import ROUTES from '@/router/routes';
import {
  getFinalWinnersMlFallback,
  getInitDesenlaceMl,
} from '@/services/minileagues';
import { EnumCoverMinileagues } from '@/types/minileagues';
import { cn, formatNumberToLocale } from '@/utils';
import { SECTION_NAME_FOR_ADS } from '@/utils/ads';

interface CardRowUserProps {
  id: number;
  position: number;
  name?: string;
  lastname?: string;
  club?: string;
  city?: string;
  prize?: number;
  points?: number;
}

interface UserStatsProps {
  userPosition: number | undefined;
  name?: string;
  club?: string;
}

const UserStats = ({ userPosition, club, name }: UserStatsProps) => {
  const { isMobile } = useIsDesktop();

  if (isMobile) {
    return (
      <div className="bg-white flex w-full p-6 rounded-lg justify-between">
        <div className="flex gap-x-3">
          <img alt="Camiseta" className="w-11 h-11" src={Camiseta} />
          <div className="flex flex-col gap-y-1">
            <span className="font-bold text-base">{name}</span>
            <span className="text-xs font-semibold text-gdt-gray-3">
              {club}
            </span>
          </div>
        </div>
        <div className="flex flex-col gap-y-1">
          <span className="font-semibold text-2xs text-gdt-gray-3">
            RANKING
          </span>
          <span className="font-bold text-gdt-status-green text-xl">
            {userPosition ? `#${userPosition}` : '-'}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white flex flex-col lg:w-[426px] p-6 rounded-lg mt-4">
      <div className="flex items-center">
        <img alt="stats" className="w-7 h-7 mr-2" src={IcStats} />
        <div className="flex flex-col">
          <span className="font-bold text-lg">Mi desempeño</span>
        </div>
      </div>
      <div className="flex ml-9 mt-7 gap-x-5">
        <span className="text-gdt-gray-3 font-semibold text-base ">
          TU POSICIÓN EN EL RANKING
        </span>
        <span className="font-bold text-gdt-status-green text-3xl">
          {userPosition ? `#${userPosition}` : '-'}
        </span>
      </div>
    </div>
  );
};

const CardRowUser = ({
  position,
  name,
  lastname,
  club,
  city,
  prize,
  points,
}: CardRowUserProps) => {
  return (
    <div className="grid grid-cols-2 py-3 px-4 items-center border-b border-gdt-gray-6 gap-4">
      {/* Column 1: Position and DT Info */}
      <div className="grid grid-cols-[auto_1fr] gap-4 items-center">
        {/* Position */}
        <div
          className={cn(
            'w-12 h-12 flex items-center justify-center rounded-full font-bold bg-gdt-status-green text-white',
            { 'bg-gdt-status-green-light text-gdt-status-green': position > 5 }
          )}
        >
          <span>{position}</span>
        </div>

        {/* DT Info */}
        <div className="flex flex-col text-left md:pl-3">
          <span className="font-bold text-sm">
            {name} {lastname}
          </span>
          <span className="text-gdt-gray-2 font-semibold text-xs">{club}</span>
          <span className="text-gdt-gray-4 text-xs font-bold">{city}</span>
        </div>
      </div>

      {/* Column 2: Prize and Points */}
      <div className="grid grid-cols-2 gap-4">
        {/* Prize - Hidden on mobile */}
        <span className="text-gdt-status-green font-bold text-sm text-center">
          {prize === 0 || !prize ? '-' : `$${formatNumberToLocale(prize ?? 0)}`}
        </span>

        {/* Points */}
        <span className="text-gdt-gray-2 font-semibold text-base text-center md:col-start-2">
          {points}
        </span>
      </div>
    </div>
  );
};

export const ViewFinalML: React.FC = () => {
  const navigate = useNavigate();
  const { data: finalWinnersData } = useQuery('initFinalMl', async () =>
    getInitDesenlaceMl()
  );
  const { userInitialData } = useAuth();
  const { tourneyData, updateTourney } = useTourney();
  const { isDesktop } = useIsDesktop();

  const [top10, setTop10] = useState<CardRowUserProps[]>(
    Array(10).fill({
      position: undefined,
      name: undefined,
      lastname: undefined,
      club: undefined,
      city: undefined,
      prize: undefined,
      points: undefined,
    })
  );

  const positionPrizeByGender = [
    [],
    [
      EnumCoverMinileagues.FirstMale,
      EnumCoverMinileagues.SecondMale,
      EnumCoverMinileagues.ThirdMale,
      EnumCoverMinileagues.FourthMale,
      EnumCoverMinileagues.FifthMale,
    ],
    [
      EnumCoverMinileagues.FirstFemale,
      EnumCoverMinileagues.SecondFemale,
      EnumCoverMinileagues.ThirdFemale,
      EnumCoverMinileagues.FourthFemale,
      EnumCoverMinileagues.FifthFemale,
    ],
  ];

  const CoverText = useMemo(() => {
    if (finalWinnersData?.publicadaResultado) {
      const userPos = top10.findIndex(
        (user) => user.id === userInitialData?.idUsuarioDT
      );
      // esta en top 5
      if (userPos > -1 && userPos < 5) {
        return positionPrizeByGender[Number(userInitialData?.idSexo ?? 1)][
          userPos
        ];
      }
      // no esta en top 5 pero si top 10
      if (userPos > 5 && userPos < 10) {
        return EnumCoverMinileagues.NoTop5;
      }
      // participa o no
      return EnumCoverMinileagues.NotParticipatedOrNotClassified;
    } else {
      // participa top 10
      if (finalWinnersData?.participaDesenlace) {
        return EnumCoverMinileagues.FinalClassified;
      }
      // no participa top 10 pero si jugo ml
      if (
        finalWinnersData?.participaML &&
        !finalWinnersData.participaDesenlace
      ) {
        return EnumCoverMinileagues.FinalPlayerNotClassified;
      }
      // no participa top 10 ni jugo ml
      return EnumCoverMinileagues.FinalPlayerNotParticipating;
    }
  }, [
    finalWinnersData?.participaDesenlace,
    finalWinnersData?.publicadaResultado,
    finalWinnersData?.participaML,
    top10,
    userInitialData?.idSexo,
    userInitialData?.idUsuarioDT,
  ]);

  useEffect(() => {
    const fetcher = async () => {
      const winners = await getFinalWinnersMlFallback(
        finalWinnersData?.idFechaDes ?? 0
      );
      setTop10(
        winners?.map((winner, index) => ({
          id: winner.id,
          position: winner.puesto,
          name: winner.nombre,
          lastname: winner.apellido,
          club: winner.nombreEquipo,
          city: winner.provincia,
          prize: finalWinnersData?.premios?.[index],
          points: winner.puntos,
        }))
      );
    };
    if (finalWinnersData?.publicadaResultado) {
      fetcher();
    } else {
      setTop10((prev) =>
        prev.map((user, index) => ({
          ...user,
          prize: finalWinnersData?.premios?.[index],
        }))
      );
    }
  }, [finalWinnersData]);

  const userPosition = top10.find(
    (user) => user.id === userInitialData?.idUsuarioDT
  )?.position;

  const userPrize = top10.find(
    (user) => user.id === userInitialData?.idUsuarioDT
  )?.prize;

  return (
    <>
      <Layout
        adsSections={SECTION_NAME_FOR_ADS.miniligas}
        className="md:px-4"
        coverTop={() => {
          return (
            <div className="px-4">
              <CoverImage
                context={CoverText}
                position={userPosition}
                prize={formatNumberToLocale(userPrize ?? 0)}
              />

              {userPosition && !isDesktop && (
                <div className="mb-8">
                  <UserStats
                    club={userInitialData?.nombre_equipo}
                    name={userInitialData?.nombre_dt}
                    userPosition={userPosition}
                  />
                </div>
              )}
              <h1 className="text-2xl font-bold mt-10 mb-3 hidden md:block">
                Top 10
              </h1>
            </div>
          );
        }}
        main={() => (
          <div className="lg:pr-4">
            {/* Header row using grid */}
            <div className="grid grid-cols-2 mb-4 px-4 font-bold text-gdt-gray-4 gap-4">
              {/* Column 1 Header */}
              <div className="grid grid-cols-[auto_1fr] gap-4">
                <span className="hidden md:block">Posición</span>
                <span className="block md:hidden pl-2 md:pl-0">Pos</span>
                <span className="text-left pl-4 md:pl-0">DT</span>
              </div>

              {/* Column 2 Header */}
              <div className="grid grid-cols-2 gap-4">
                <span className="text-center ">Premio</span>
                <span className="text-center md:col-start-2">Puntos</span>
              </div>
            </div>
            <div className="bg-white flex flex-col rounded-lg">
              {top10.map((user, index) => (
                <CardRowUser
                  key={index}
                  city={user.city}
                  club={user.club}
                  id={user.id}
                  lastname={user.lastname}
                  name={user.name}
                  points={user.points}
                  position={index + 1}
                  prize={user.prize}
                />
              ))}
            </div>
          </div>
        )}
        mobile={{
          leftContent: {
            showMenuAction: true,
            items: true,
          },
          centerContent: { title: 'Miniligas' },
        }}
        sidebar={() => (
          <div className="flex flex-col mt-5">
            {finalWinnersData?.participaML && (
              <Button
                className=" mx-auto flex justify-center items-center font-bold mt-4 md:mt-0"
                type="button"
                variant="text"
                onClick={() => {
                  navigate(ROUTES.minileagues.home, {
                    state: { comesFromFinal: true },
                  });
                }}
              >
                Ver resultados mis Miniligas
              </Button>
            )}
            {isDesktop && userPosition ? (
              <UserStats userPosition={userPosition} />
            ) : (
              <div className="lg:w-[426px] px-6 rounded-lg" />
            )}
            <Button
              className="mt-6 mx-auto flex justify-center items-center font-bold"
              type="button"
              variant="text"
              onClick={() => {
                isDesktop
                  ? updateTourney({ showModalInfo: true })
                  : navigate(ROUTES.minileagues.info);
              }}
            >
              Más información y Premios
            </Button>
          </div>
        )}
      />
      <Modal
        className="w-[500px] text-center px-[38px]"
        controlledOnClose={() => updateTourney({ showModalInfo: false })}
        isControlledOpen={Boolean(tourneyData.showModalInfo)}
        render={() => <InfoContent />}
        size="sm"
        variant="controlled"
      />
    </>
  );
};
